import { Button, Modal, Select, useToast } from "@nabble/ui";
import { useState } from "react";
import type { Organization } from "../../../api/adminOrganizations";
import type { Role } from "../../../api/adminRoles";
import { removeUserMembership, setUserMembership, type AdminUser } from "../../../api/adminUsers";
import { RolePicker } from "../../../components/RolePicker";
import { errorMessage } from "../../../errors";
import { DEFAULT_ROLE_IDS } from "../../../model/organizationRoles";
import { MembershipRow } from "./MembershipRow";

interface UserMembershipsDialogProps {
  user: AdminUser;
  organizations: Organization[];
  roles: Role[];
  onClose: () => void;
  onSaved: () => void;
}

/** The organizations a user belongs to and the roles they hold in each: change, remove or add. */
export function UserMembershipsDialog({ user, organizations, roles, onClose, onSaved }: UserMembershipsDialogProps): React.JSX.Element {
  const { show } = useToast();
  const [busy, setBusy] = useState(false);
  const available = organizations.filter((o) => !user.memberships.some((m) => m.organizationId === o.id));
  const [newOrganization, setNewOrganization] = useState<string>(available[0]?.id ?? "");
  const [newRoles, setNewRoles] = useState<string[]>([...DEFAULT_ROLE_IDS]);
  const nameOf = (id: string): string => organizations.find((o) => o.id === id)?.name ?? "";

  async function run(action: () => Promise<void>, done: string): Promise<void> {
    setBusy(true);
    try {
      await action();
      show({ title: done, tone: "success" });
      onSaved();
    } catch (err) {
      show({ title: "We couldn't save the change", text: errorMessage(err, "Please try again."), tone: "danger" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      icon="briefcase"
      title={`Organizations of ${user.displayName}`}
      description="People only see the work of the organizations they belong to. In each one they get every permission of the roles they hold there."
      onClose={onClose}
      actions={<Button label="Done" variant="secondary" onClick={onClose} />}
    >
      <div className="nb-stack">
        {user.memberships.length === 0 ? <p className="nb-help">They don't belong to any organization yet.</p> : null}
        {user.memberships.map((m) => (
          <MembershipRow
            key={m.organizationId}
            organizationName={nameOf(m.organizationId)}
            held={m.roles}
            roles={roles}
            busy={busy}
            onSave={(roleIds) => run(() => setUserMembership(user.id, m.organizationId, roleIds), "Roles saved")}
            onRemove={() => void run(() => removeUserMembership(user.id, m.organizationId), "Removed from the organization")}
          />
        ))}
        {available.length > 0 ? (
          <div className="nb-stack" style={{ borderTop: "1px solid var(--line)", paddingTop: "var(--space-3)" }}>
            <span className="nb-label">Add to an organization</span>
            <Select value={newOrganization} onValueChange={setNewOrganization} aria-label="Organization" options={available.map((o) => ({ value: o.id, label: o.name }))} />
            <RolePicker roles={roles} value={newRoles} onChange={setNewRoles} label="Roles there" showLabel />
            <div>
              <Button
                label="Add"
                icon="plus"
                disabled={busy || !newOrganization || newRoles.length === 0}
                onClick={() => void run(() => setUserMembership(user.id, newOrganization, newRoles), `Added to ${nameOf(newOrganization)}`)}
              />
            </div>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
